import { isVideo } from "@/animation/video-generator";
import { Button } from "@/components/atoms/Button/Button";
import * as windowManager from "@/overlay/components/window";
import type { Changelog, MediaItem } from "@/repository/mapper/ContentMapper";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { type WindowElement, type WindowIdentityProps, type WindowKind, useDesktop } from "../window";

const MediaBlock = ({ mediaItem }: { mediaItem: MediaItem }) => {
  if (!mediaItem.typeMime) {
    console.error("Media item has no typeMime", mediaItem);
    return;
  }

  let media = null;

  if (!isVideo(mediaItem.typeMime)) {
    media = <img src={mediaItem.mediaUrl} className="feature-media-image" aria-label={mediaItem.description} />;
  } else {
    media = (
      <video src={mediaItem.mediaUrl} className="feature-media-video" controls>
        <track kind="captions" src="" label="No captions available" />
      </video>
    );
  }

  return (
    <div className="feature-media-item">
      {mediaItem.description && <div className="feature-media-description">{mediaItem.description}</div>}
      {media}
    </div>
  );
};

type ExploreModalProps = {
  changelog?: Changelog;
  closeWindow?: () => void;
};
const ExploreModal = ({ changelog, closeWindow }: ExploreModalProps) => {
  if (!changelog) {
    return <div>Loading...</div>;
  }

  return (
    <div className="explore-modal">
      <div className="explore-modal__title">{changelog.title}</div>
      <div
        className="explore-modal__title-description"
        // biome-ignore lint/security/noDangerouslySetInnerHtml: very dangerous
        dangerouslySetInnerHTML={{ __html: changelog.overallDescription || "" }}
      />
      <div className="explore-modal__features-header">
        <Trans>New Features</Trans>
      </div>

      <div className="explore-modal__feature-list">
        {changelog.features.map((feature, index) => (
          <div className="explore-modal__feature-item" key={`${feature.title}-${index}`}>
            <div className="explore-modal__feature-text">
              <div className="explore-modal__feature-title">{feature.title}</div>
              <div
                className="explore-modal__feature-description"
                // biome-ignore lint/security/noDangerouslySetInnerHtml: very dangerous
                dangerouslySetInnerHTML={{ __html: feature.richDescription }}
              />
            </div>
            <div className="explore-modal__feature-media-list">
              {feature.media.map((mediaItem) => (
                <MediaBlock mediaItem={mediaItem} key={mediaItem.mediaUrl} />
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="explore-modal__footer">
        <Button className="explore-modal__close-button" onClick={closeWindow}>{t`Close`}</Button>
      </div>
    </div>
  );
};

export type ExploreWindowProps = {
  changelogData: Changelog;
};
export default function ExploreWindow(props: WindowIdentityProps<WindowKind.ExploreWindow>): WindowElement {
  const desktop = useDesktop();

  function closeWindow() {
    desktop.dispatch({ type: "setWindowVisibility", windowId: props.instanceId, isVisible: false });
  }
  const actions = [windowManager.closeAction(closeWindow)];

  return (
    <windowManager.Window kind={"ExploreWindow"} title={t`Release Notes`} actions={actions} {...props}>
      <ExploreModal changelog={props.customProps.changelogData} closeWindow={closeWindow} />
    </windowManager.Window>
  );
}
